import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { useChangePassword } from '@/domains/users/services/account-users';
import { validatePassword, getPasswordStrengthColor, getPasswordStrengthLabel } from '@/shared/utils/password-validator';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { User } from '@/shared/types/userTypes';

interface ChangePasswordDialogProps {
  isOpen: boolean;
  onClose: (onChangePasswordSuccess?: boolean) => void;
  user: User;
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  isOpen,
  onClose,
  user,
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const changePassword = useChangePassword();

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setConfirmPassword('');
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [isOpen]);

  const passwordValidation = validatePassword(password);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password
    if (!passwordValidation.isValid) {
      showErrorWithApiError({ message: ERROR.PASSWORD.WEAK_PASSWORD }, ERROR.PASSWORD.WEAK_PASSWORD);
      return;
    }

    // Validate passwords match
    if (!passwordsMatch) {
      showErrorWithApiError({ message: ERROR.PASSWORD.PASSWORDS_DONT_MATCH }, ERROR.PASSWORD.PASSWORDS_DONT_MATCH);
      return;
    }

    try {
      await changePassword.mutateAsync({
        userId: user.userId,
        password,
      });

      showSuccess(SUCCESS.PASSWORD.CHANGE_SUCCESS);
      onClose(true);
    } catch (error) {
      showErrorWithApiError(error, ERROR.PASSWORD.CHANGE_FAILED);
    }
  };

  const canSubmit = passwordValidation.isValid && passwordsMatch && !changePassword.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription>
            Enter your new password. Make sure it meets all the security requirements.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* New Password Field */}
          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>

            {/* Password Validation Feedback */}
            {password && (
              <div className="text-xs space-y-1 mt-2">
                <div className={`flex items-center ${passwordValidation.minLength ? 'text-green-500' : 'text-red-500'}`}>
                  <span className="mr-1">{passwordValidation.minLength ? '✓' : '✗'}</span>
                  At least 12 characters
                </div>
                <div className={`flex items-center ${passwordValidation.hasUpperCase ? 'text-green-500' : 'text-red-500'}`}>
                  <span className="mr-1">{passwordValidation.hasUpperCase ? '✓' : '✗'}</span>
                  One uppercase letter
                </div>
                <div className={`flex items-center ${passwordValidation.hasLowerCase ? 'text-green-500' : 'text-red-500'}`}>
                  <span className="mr-1">{passwordValidation.hasLowerCase ? '✓' : '✗'}</span>
                  One lowercase letter
                </div>
                <div className={`flex items-center ${passwordValidation.hasNumbers ? 'text-green-500' : 'text-red-500'}`}>
                  <span className="mr-1">{passwordValidation.hasNumbers ? '✓' : '✗'}</span>
                  One number
                </div>
                <div className={`flex items-center ${passwordValidation.hasSpecialChar ? 'text-green-500' : 'text-red-500'}`}>
                  <span className="mr-1">{passwordValidation.hasSpecialChar ? '✓' : '✗'}</span>
                  One special character
                </div>
                <div className={`flex items-center ${passwordValidation.isNotEasilyGuessable ? 'text-green-500' : 'text-red-500'}`}>
                  <span className="mr-1">{passwordValidation.isNotEasilyGuessable ? '✓' : '✗'}</span>
                  Not easily guessable
                </div>
                {/* Password Strength Indicator */}
                {password.length > 0 && (
                  <div className={`mt-2 pt-2 border-t border-border flex items-center justify-between`}>
                    <span>Strength:</span>
                    <span className={`font-medium ${getPasswordStrengthColor(passwordValidation.strength)}`}>
                      {getPasswordStrengthLabel(passwordValidation.strength)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Confirm Password Field */}
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
            {confirmPassword && !passwordsMatch && (
              <p className="text-xs text-red-500">Passwords do not match</p>
            )}
            {confirmPassword && passwordsMatch && (
              <p className="text-xs text-green-500">✓ Passwords match</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onClose(false)} disabled={changePassword.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit} className="min-w-[100px]">
              {changePassword.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Changing...
                </>
              ) : (
                'Change Password'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
